import {useEffect} from 'react'

import {defaultBrand} from '@vio/core/src/brand/config'
import {BrandThemeName} from '@vio/core/src/brand/types'

import {
  FALLBACK_FONT_LANGUAGES,
  fontFaces,
  fontWeights,
  languageFontSettings,
  letterSpacingAdjust,
  nonLatinFontFamilies
} from './constants'

type PropsType = {
  theme?: BrandThemeName
  languageCode?: string
}

const getFontFamilyCssVariable: (
  languageCode: string,
  theme?: BrandThemeName
) => string = (languageCode: string = 'en-GB') => {
  if (FALLBACK_FONT_LANGUAGES.has(languageCode)) {
    return `${nonLatinFontFamilies[languageCode as keyof typeof nonLatinFontFamilies]}, Helvetica Neue, Helvetica, Arial, "Lucida Grande", sans-serif`
  }
  return `"Campton", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif`
}

const getFontFamilyImportPath = () => {
  const familyNames = new Set([...Object.values(nonLatinFontFamilies)])
  return Array.from(familyNames)
    .map(name => name.replace(/\s+/g, '+'))
    .join(':wght@400..500&family=')
}

const getLetterSpacingAdjustments: (
  languageCode: string,
  theme?: BrandThemeName
) => string = languageCode => {
  const adjustments = FALLBACK_FONT_LANGUAGES.has(languageCode)
    ? languageFontSettings[languageCode as keyof typeof languageFontSettings]
        .letterSpacingAdjust
    : letterSpacingAdjust.default

  return Object.keys(adjustments)
    .map(
      key =>
        `--letterSpacingAdjust-${key}: ${adjustments[key as keyof typeof adjustments]};`
    )
    .join('\n')
}

const getFontWeightCssVariables: (theme?: BrandThemeName) => string = () => {
  const adjustments = fontWeights.default
  return Object.keys(adjustments)
    .map(
      key =>
        `--textFontWeight-${key}: ${adjustments[key as keyof typeof adjustments]};`
    )
    .join('\n')
}

export const getTypographyCssVariables = (
  languageCode: string,
  theme: BrandThemeName = defaultBrand.theme
) => {
  return `
    :root {
      --fontFamily: ${getFontFamilyCssVariable(languageCode, theme)};
      ${getLetterSpacingAdjustments(languageCode, theme)};
      ${getFontWeightCssVariables(theme)}
    }`
}

const fallbackFonts = `
  @import url('https://fonts.googleapis.com/css2?family=${getFontFamilyImportPath()}&display=swap');
`

export const getFontFaces: (
  languageCode: string,
  theme?: BrandThemeName
) => string = (languageCode: string) => {
  if (FALLBACK_FONT_LANGUAGES.has(languageCode)) return fallbackFonts
  return fontFaces.default
}

/**
 * @deprecated Only use in Storybook. For apps, use @vio/shared/src/utils/FontsLoaderNext/FontsLoaderNext.tsx instead.
 */
const FontsLoaderStorybook = ({
  languageCode = 'en-GB',
  theme = defaultBrand.theme
}: PropsType) => {
  useEffect(() => {
    const styles = `${getFontFaces(languageCode, theme)}\n${getTypographyCssVariables(languageCode)}`
    const style = document.createElement('style')
    style.innerHTML = styles
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [languageCode, theme])

  return null
}

export default FontsLoaderStorybook
